/**
 * Symmetric encryption utilities (AES-256-GCM).
 * Key derived from resolved AUTH_SECRET via HKDF (RFC 5869).
 * Format: base64(iv):base64(authTag):base64(ciphertext)
 */

import { createCipheriv, createDecipheriv, randomBytes, hkdfSync } from 'node:crypto'
import { getAuthSecret } from './secret-resolver.js'

const ALGORITHM = 'aes-256-gcm'
const IV_LENGTH = 12
const KEY_LENGTH = 32
/** Domain separation info string — ensures this key is unique to Nexus symmetric encryption */
const HKDF_INFO = 'nexus-symmetric-encryption-v1'

let derivedKey: Buffer | null = null

function getKey(): Buffer {
  if (derivedKey) return derivedKey
  const secret = getAuthSecret()
  // HKDF: extract-then-expand. Empty salt is valid per RFC 5869 §3.1
  // (uses hash-length zero bytes). Info string provides domain separation.
  const keyBytes = hkdfSync('sha256', secret, '', HKDF_INFO, KEY_LENGTH)
  derivedKey = Buffer.from(keyBytes)
  return derivedKey
}

/**
 * Encrypts plaintext using AES-256-GCM.
 * Returns format: base64(iv):base64(authTag):base64(ciphertext)
 */
export function encrypt(plaintext: string): string {
  const key = getKey()
  const iv = randomBytes(IV_LENGTH)
  const cipher = createCipheriv(ALGORITHM, key, iv)
  const encrypted = Buffer.concat([cipher.update(plaintext, 'utf8'), cipher.final()])
  const authTag = cipher.getAuthTag()
  return `${iv.toString('base64')}:${authTag.toString('base64')}:${encrypted.toString('base64')}`
}

/**
 * Decrypts ciphertext produced by encrypt().
 */
export function decrypt(ciphertext: string): string {
  const key = getKey()
  const parts = ciphertext.split(':')
  if (parts.length !== 3) {
    throw new Error('Invalid ciphertext format: expected base64(iv):base64(authTag):base64(data)')
  }
  const [ivB64, tagB64, dataB64] = parts
  const iv = Buffer.from(ivB64!, 'base64')
  const authTag = Buffer.from(tagB64!, 'base64')
  const data = Buffer.from(dataB64!, 'base64')
  const decipher = createDecipheriv(ALGORITHM, key, iv)
  decipher.setAuthTag(authTag)
  return Buffer.concat([decipher.update(data), decipher.final()]).toString('utf8')
}

/**
 * Resets derived key cache (for testing only).
 */
export function _resetKey(): void {
  derivedKey = null
}
